/**
 * MessageBubble - メッセージコンポーネント
 */

import { AttachedFilesList } from '@/components/attachment/AttachedFilesList';
import { TypingIndicator } from '@/components/loading/TypingIndicator';
import { AttachedFileInfo } from '@/types';
import { Box, BoxProps } from '@chakra-ui/react';

interface MessageBubbleProps extends Omit<BoxProps, 'content'> {
  role: 'user' | 'assistant';
  content: React.ReactNode;
  avatar?: React.ReactNode;
  isError?: boolean;
  isLoading?: boolean;
  attachedFiles?: AttachedFileInfo[];
  generatedImage?: React.ReactNode;
}

export const MessageBubble = ({
  role,
  content,
  avatar,
  isError = false,
  isLoading = false,
  attachedFiles,
  generatedImage,
  ...props
}: MessageBubbleProps) => {
  const isUser = role === 'user';

  const containerStyle = isError
    ? {
        borderBottom: '1px solid',
        borderColor: 'gray.200',
        borderLeft: '4px solid',
        borderLeftColor: 'semantics.error',
        bg: 'red.50',
        px: 0,
        py: 6,
        _last: { borderBottom: 0 },
      }
    : {
        borderBottom: '1px solid',
        borderColor: 'gray.200',
        bg: isUser ? 'surfaces.user' : 'surfaces.ai',
        px: 0,
        py: 6,
        _last: { borderBottom: 0 },
      };

  // コンテンツ配置: ユーザーは右寄せ、AIは左寄せ
  const contentAlignment = isUser ? 'flex-end' : 'flex-start';
  const textAlignment = isUser ? 'right' : 'left';

  return (
    <Box {...containerStyle} {...props}>
      <Box
        display="flex"
        mx="auto"
        alignItems="flex-start"
        gap={5}
        px={6}
        justifyContent={contentAlignment}
      >
        {/* AIメッセージの場合、アバターを左側に表示 */}
        {!isUser && avatar && (
          <Box
            display="flex"
            w={9}
            h={9}
            flexShrink={0}
            alignItems="center"
            justifyContent="center"
            borderRadius="md"
            boxShadow="sm"
          >
            {avatar}
          </Box>
        )}

        {/* メッセージコンテンツ */}
        <Box minW={0} maxW="85%" flex={1} py={0.5}>
          {/* 添付ファイル */}
          {attachedFiles && attachedFiles.length > 0 && <AttachedFilesList files={attachedFiles} />}

          {/* メッセージテキスト */}
          {isLoading ? (
            <TypingIndicator />
          ) : (
            <>
              <Box
                m={0}
                whiteSpace={isUser ? 'pre-wrap' : 'normal'}
                wordBreak="break-word"
                fontSize="lg"
                fontWeight="normal"
                lineHeight="tall"
                color="gray.700"
                textAlign={textAlignment}
              >
                {content}
              </Box>

              {/* 生成された画像 */}
              {generatedImage && generatedImage}
            </>
          )}
        </Box>

        {/* ユーザーメッセージの場合、アバターを右側に表示 */}
        {isUser && avatar && (
          <Box
            display="flex"
            w={9}
            h={9}
            flexShrink={0}
            alignItems="center"
            justifyContent="center"
            borderRadius="md"
            bg="semantics.success"
            fontSize="md"
            fontWeight="semibold"
            color="white"
            boxShadow="sm"
          >
            {avatar}
          </Box>
        )}
      </Box>
    </Box>
  );
};
